import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Sunrise, TrendingUp, BadgePercent, Wallet, ShieldAlert, Rocket, Sparkles, ListChecks, Inbox, ArrowRight,
} from "lucide-react";
import api from "../lib/api";
import { TypeBadge, SeverityBadge } from "../components/Badges";

const StatCard = ({ label, value, tone, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
    <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">{label}</div>
    <div className={`mt-1 text-2xl md:text-3xl font-extrabold font-mono tracking-tight ${tone}`}>{value}</div>
  </div>
);

const Section = ({ title, icon: Icon, items, empty, testid, tone = "text-slate-700 dark:text-slate-300" }) => {
  const navigate = useNavigate();
  return (
    <section data-testid={testid} className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${tone}`} />
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{title}</h2>
        <span className="ml-auto rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-sm font-mono text-slate-600 dark:text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-base text-slate-600 dark:text-slate-400">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 5).map((e) => (
            <li
              key={e.id}
              data-testid={`brief-item-${e.id}`}
              onClick={() => navigate(`/events/${e.id}`)}
              className="group cursor-pointer rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="flex items-center gap-2">
                <TypeBadge type={e.event_type} />
                {e.due_date && <span className="text-sm font-mono text-cyan-600 dark:text-cyan-400">due {e.due_date}</span>}
                <ArrowRight className="ml-auto h-3 w-3 text-slate-600 dark:text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors" />
              </div>
              <p className="mt-1 text-base text-slate-700 dark:text-slate-300 leading-snug">{e.title}</p>
              <p className="mt-0.5 text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{e.vendor_name}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const DailyBrief = () => {
  const [brief, setBrief] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/brief").then((r) => {
      setBrief(r.data);
      api.post("/brief/mark-seen").catch(() => {});
    }).catch(() => {});
  }, []);

  if (!brief) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="brief-loading">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
      </div>
    );
  }

  const s = brief.stats;
  return (
    <div data-testid="daily-brief-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <Sunrise className="h-5 w-5" />
            <span className="text-sm font-mono uppercase tracking-widest">Daily CEO Brief</span>
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {format(new Date(), "EEEE, d MMMM")}
          </h1>
        </div>
        <Link
          to="/capture"
          data-testid="brief-capture-cta"
          className="rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
        >
          + Capture a forward
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="exec-summary-bar">
        <StatCard testid="stat-brands" label="Brands tracked" value={s.brands} tone="text-slate-900 dark:text-slate-100" />
        <StatCard testid="stat-confirms" label="Needs confirm" value={s.confirm_pending} tone={s.confirm_pending ? "text-amber-600 dark:text-amber-400" : "text-slate-900 dark:text-slate-100"} />
        <StatCard testid="stat-actions" label="Actions required" value={s.actions_required} tone={s.actions_required ? "text-rose-600 dark:text-rose-400" : "text-slate-900 dark:text-slate-100"} />
        <StatCard testid="stat-articles" label="Catalog articles" value={s.articles} tone="text-cyan-600 dark:text-cyan-400" />
      </div>

      {brief.new_since_last_visit && brief.new_since_last_visit.length > 0 && (
        <section data-testid="whats-new-panel" className="rounded-lg border border-cyan-300 dark:border-cyan-500/40 bg-cyan-50 dark:bg-cyan-500/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
            <h2 className="text-base font-semibold text-cyan-800 dark:text-cyan-300">
              New since your last visit ({brief.new_since_last_visit.length})
            </h2>
          </div>
          <ul className="space-y-2">
            {brief.new_since_last_visit.slice(0, 6).map((e) => (
              <li
                key={e.id}
                data-testid={`whats-new-item-${e.id}`}
                onClick={() => navigate(`/events/${e.id}`)}
                className="cursor-pointer rounded-md border border-cyan-200 dark:border-cyan-500/30 bg-white dark:bg-slate-900 px-3 py-2 hover:bg-cyan-100 dark:hover:bg-cyan-500/15 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <TypeBadge type={e.event_type} />
                  <span data-testid="whats-new-badge" className="rounded bg-cyan-500 px-1.5 py-0.5 text-xs font-bold font-mono uppercase tracking-wider text-white">New</span>
                  <ArrowRight className="ml-auto h-3 w-3 text-cyan-600 dark:text-cyan-400" />
                </div>
                <p className="mt-1 text-base text-slate-700 dark:text-slate-300 leading-snug">{e.title}</p>
                <p className="mt-0.5 text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{e.vendor_name}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {brief.confirm_queue.length > 0 && (
        <div
          data-testid="confirm-queue-banner"
          onClick={() => navigate("/confirm")}
          className="cursor-pointer rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 flex items-center gap-3 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
        >
          <Inbox className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <div>
            <div className="text-base font-semibold text-amber-700 dark:text-amber-300">
              {brief.confirm_queue.length} event{brief.confirm_queue.length > 1 ? "s" : ""} need your confirm
            </div>
            <div className="text-sm text-amber-600/70 dark:text-amber-400/70">Low-confidence AI classifications — approve, re-classify or reject.</div>
          </div>
          <ArrowRight className="ml-auto h-4 w-4 text-amber-600 dark:text-amber-400" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        <div className="md:col-span-6">
          <Section testid="brief-price-changes" title="Price changes" icon={TrendingUp} tone="text-amber-600 dark:text-amber-400" items={brief.price_changes} empty="No price revisions captured." />
        </div>
        <div className="md:col-span-6">
          <Section testid="brief-expiring-schemes" title="Expiring schemes & offers" icon={BadgePercent} tone="text-emerald-600 dark:text-emerald-400" items={brief.expiring_schemes} empty="No schemes expiring in the next 30 days." />
        </div>
        <div className="md:col-span-6">
          <Section testid="brief-payments-due" title="Payments due & ledgers" icon={Wallet} tone="text-red-600 dark:text-red-400" items={brief.payments_due} empty="No payment events on the radar." />
        </div>
        <div className="md:col-span-6">
          <Section testid="brief-compliance" title="Credit policy & compliance" icon={ShieldAlert} tone="text-orange-600 dark:text-orange-400" items={brief.compliance} empty="No policy or compliance notices." />
        </div>
        <div className="md:col-span-6">
          <Section testid="brief-launches" title="Product launches" icon={Rocket} tone="text-cyan-600 dark:text-cyan-400" items={brief.launches} empty="No launches captured." />
        </div>
        <div className="md:col-span-6">
          <Section testid="brief-opportunities" title="Flagged opportunities" icon={Sparkles} tone="text-indigo-600 dark:text-indigo-400" items={brief.opportunities} empty="No opportunities flagged." />
        </div>
      </div>

      <section data-testid="brief-action-items" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="h-4 w-4 text-rose-600 dark:text-rose-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Action items</h2>
        </div>
        {brief.high_impact.length === 0 && brief.open_tasks.length === 0 ? (
          <p className="text-base text-slate-600 dark:text-slate-400">Nothing urgent. Enjoy the quiet morning.</p>
        ) : (
          <ul className="space-y-2">
            {brief.high_impact.map((e) => (
              <li
                key={e.id}
                data-testid={`action-item-${e.id}`}
                onClick={() => navigate(`/events/${e.id}`)}
                className="cursor-pointer rounded-md border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 hover:bg-rose-100 dark:hover:bg-rose-500/15 transition-colors"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <SeverityBadge severity={e.severity} note={e.impact_note} />
                  <span className="text-base font-medium text-slate-800 dark:text-slate-200">{e.title}</span>
                </div>
                {e.recommended_action && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{e.recommended_action}</p>}
              </li>
            ))}
            {brief.open_tasks.map((t) => (
              <li
                key={t.id}
                data-testid={`task-item-${t.id}`}
                onClick={() => navigate(`/events/${t.event_id}`)}
                className="cursor-pointer rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <span className="text-base text-slate-700 dark:text-slate-300">{t.title}</span>
                <span className="ml-2 text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{t.event_title}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default DailyBrief;
